<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PaidPlanBadge from '@/components/dashboard/ui/PaidPlanBadge.vue'
import { useClaimTaxBack } from '@/composables/useClaimTaxBack'
import givingPreviewDesktop from '@/assets/images/swiftaid-donor-dashboard-desktop.jpg'
import givingPreviewMobile from '@/assets/images/swiftaid-donor-dashboard-mobile.jpg'
import debug from '@/helpers/debug'

const emit = defineEmits<{
  upgrade: []
}>()

const { t } = useI18n()

// The claim guide is a paid feature, so it appears here as a bullet only when
// the claim API is available. Discovery is driven by the dashboard
// notification and the sidebar item, not by this card.
const { isClaimAvailable } = useClaimTaxBack()

const features = computed(() => {
  const list = [
    t('Dashboard.Home.Consolidation.Features.0'),
    t('Dashboard.Home.Consolidation.Features.1'),
    t('Dashboard.Home.Consolidation.Features.2'),
  ]
  if (isClaimAvailable.value) {
    list.push(t('Dashboard.Home.Consolidation.FeatureClaimGuide'))
  }
  return list
})

// Same product shots as the donations page, so the upgrade card shows the
// giving record donors are being asked to unlock.
watch(
  isClaimAvailable,
  (claimGuide) => {
    debug('[home] consolidation card', { claimGuide })
  },
  { immediate: true }
)
</script>

<template>
  <div class="sa-card sa-consolidation-card">
    <div class="sa-consolidation-card__intro">
      <div class="sa-consolidation-card__icon" aria-hidden="true">
        <PaidPlanBadge />
      </div>
      <h2 class="sa-consolidation-card__title">
        {{ $t('Dashboard.Home.Consolidation.Title') }}
      </h2>
      <p class="sa-consolidation-card__body">
        {{ $t('Dashboard.Home.Consolidation.Body') }}
      </p>
    </div>

    <!-- Decorative. The heading and feature list already say what this is. -->
    <div class="sa-consolidation-card__stage" aria-hidden="true">
      <picture>
        <source media="(max-width: 759.98px)" :srcset="givingPreviewMobile" />
        <img
          :src="givingPreviewDesktop"
          alt=""
          width="1233"
          height="1024"
          decoding="async"
        />
      </picture>
    </div>

    <div class="sa-consolidation-card__actions">
      <ul class="sa-feature-list">
        <li v-for="feature in features" :key="feature">
          <div class="sa-check-icon" aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <span>{{ feature }}</span>
        </li>
      </ul>
      <button
        type="button"
        class="sa-btn-purple sa-btn-purple--block"
        @click="emit('upgrade')"
      >
        {{ $t('Dashboard.Home.UpgradeCta') }}
        <PaidPlanBadge />
      </button>
    </div>
  </div>
</template>
